import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, onBeforeMount, onBeforeUnmount, watch } from 'vue';
import { getFixedSideCcy, getSellCcy, getBuyCcy, Trade } from 'ah-api-gateways';
import { TradeDestination } from 'ah-trades/src/components/forms/tradeDestination';
import { formatDate } from 'ah-common-lib/src/helpers/time';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import TradeInfo from './TradeInfo.vue';
import TradeBeneficiaryInfo from './TradeBeneficiaryInfo.vue';
import { useTradeState } from '../..';
import { isToday, isTomorrow } from 'date-fns';
import { TradeFundsDestination } from 'ah-trades/src/models/trade';
import { DrawdownQuotePriceResponse, getCcyByCurrency } from 'ah-api-gateways/models';
import { isPriceExpired } from 'ah-api-gateways/models';
import UpdatedDate from 'ah-common-lib/src/common/components/time/UpdatedDate.vue';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { scrollToElement } from 'ah-common-lib/src/form/helpers';
import { DrawdownDetails } from 'ah-api-gateways/models';


export default /*#__PURE__*/_defineComponent({
  __name: 'DrawdownReview',
  props: {
    trade: null,
    drawdownPrice: null,
    drawdownDetails: null,
    drawdownDestination: null,
    pricesInvalid: { type: Boolean },
    responseTimeOffset: null
  },
  emits: ["update:drawdownPrice", "update:pricesInvalid", "update:responseTimeOffset"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:drawdownPrice', value: DrawdownQuotePriceResponse | null): void;
  (e: 'update:pricesInvalid', value: boolean | null): void;
  (e: 'update:responseTimeOffset', value: number | null): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  trade: Trade;
  drawdownPrice: DrawdownQuotePriceResponse;
  drawdownDetails: DrawdownDetails;
  drawdownDestination: TradeDestination;
  pricesInvalid: boolean;
  responseTimeOffset: number;
};





const state = reactive<{
  needsPriceRefresh: boolean;
  recheckRefreshTimeout: number | null;
  offset?: number;
}>({
  needsPriceRefresh: false,
  recheckRefreshTimeout: null,
});

function goToElement() {
  const elem = document.getElementById('reloadPrice');
  if (elem) {
    scrollToElement(elem);
  }
}

watch(
  () => state.needsPriceRefresh,
  () => {
    if (state.needsPriceRefresh) {
      // WAIT FOR TRANSACTION
      window.setTimeout(() => goToElement(), 200);
      emit('update:pricesInvalid', true);
    } else {
      emit('update:pricesInvalid', false);
    }
  },
  { immediate: true }
);

onBeforeMount(recheckRefresh);

onBeforeUnmount(() => {
  if (state.recheckRefreshTimeout) {
    clearTimeout(state.recheckRefreshTimeout);
  }
});

function recheckRefresh() {
  state.needsPriceRefresh = !!(props.drawdownPrice && isPriceExpired(props.drawdownPrice, props.responseTimeOffset));
  state.recheckRefreshTimeout = window.setTimeout(() => recheckRefresh(), 1000);
}

const tradeState = useTradeState();

const sellCcy = computed(() => getSellCcy(props.drawdownPrice));

const buyCcy = computed(() => getBuyCcy(props.drawdownPrice));

const fixedSideCcy = computed(() => getFixedSideCcy(props.drawdownPrice));

const isSend = computed(() => props.drawdownDestination.destination === TradeFundsDestination.SEND);

const onBehalfOfClient = useOnBehalfOf();

const settlementLabel = computed(() => {
  if (isToday(new Date(props.drawdownPrice.settlementDate!))) {
    return 'today';
  } else if (isTomorrow(new Date(props.drawdownPrice.settlementDate!))) {
    return 'tomorrow';
  }
  return 'the day of settlement';
});

const newTrade = computed(() => {
  return {
    ...props.trade,
    ccy1: {
      ...props.trade.ccy1,
      clientAmount: props.trade.ccy1.remainingClientAmount,
      remainingClientAmount:
        props.trade.ccy1.remainingClientAmount -
        getCcyByCurrency(props.drawdownPrice, props.trade.ccy1.currency).clientAmount,
    },
    ccy2: {
      ...props.trade.ccy2,
      clientAmount: props.trade.ccy2.remainingClientAmount,
      remainingClientAmount:
        props.trade.ccy2.remainingClientAmount -
        getCcyByCurrency(props.drawdownPrice, props.trade.ccy2.currency).clientAmount,
    },
    hasDrawdowns: true,
  };
});

const tradeSellCcy = computed(() => getSellCcy(newTrade.value));

const tradeBuyCcy = computed(() => getBuyCcy(newTrade.value));

const requestManager = useRequestManager();

function refreshPricing() {
  let date = new Date();
  requestManager.manager
    .sameOrCancelAndNew(
      'getPrices',
      tradeState.services.pricingEngine.createDrawdownQuote(
        props.trade.id,
        props.drawdownDetails.amount,
        props.drawdownDetails.amountType,
        onBehalfOfClient.value?.id
      )
    )
    .subscribe((res) => {
      emit('update:responseTimeOffset', date.getTime() - new Date(res.priceRequestedTimestamp).getTime());
      emit('update:drawdownPrice', res);
    });
}

return { __sfc: true,emit, props, state, goToElement, recheckRefresh, tradeState, sellCcy, buyCcy, fixedSideCcy, isSend, onBehalfOfClient, settlementLabel, newTrade, tradeSellCcy, tradeBuyCcy, requestManager, refreshPricing, formatDate, formatCurrencyValue, TradeInfo, TradeBeneficiaryInfo, UpdatedDate }
}

})